import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toast';
import { LuCircleCheck, LuCircleX, LuExternalLink, LuLoaderCircle } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { Breadcrumbs, ErrorState, LoadingState, PageScaffold } from '../ui';
import '../../styles/environment.css';

const DESCRIPTION_MAX = 250;

const isHttpUrl = (value) => {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * CONFIGURE · Create / Edit Environment.
 */
const EnvironmentFormPage = () => {
  const navigate = useNavigate();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const envId = searchParams.get('env_id');
  const isEdit = Boolean(envId);
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [urlCheck, setUrlCheck] = useState({ status: null, message: '' });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: { environment_name: '', environment_description: '', url: '' },
    mode: 'onBlur',
  });

  const description = watch('environment_description') || '';
  const url = watch('url') || '';

  const listPath = useCallback(() => {
    const subPath = `project-environment-list/${projectId}`;
    const workspaceId = getWorkspaceIdFromURL();
    return workspaceId
      ? buildWorkspaceDashboardPath(workspaceId, getProjectIdFromURL() || projectId, subPath)
      : `/dashboard/${subPath}`;
  }, [getWorkspaceIdFromURL, getProjectIdFromURL, projectId]);

  const loadEnvironment = useCallback(async () => {
    if (!envId) return;
    setInitialLoading(true);
    setLoadError(null);
    try {
      const res = await axiosInstance.get('/user_profile/get_environment', { params: { env_id: envId } });
      if (res?.status_code !== 200) throw new Error(res?.message || 'Failed to load environment');
      const env = res.data || {};
      reset({
        environment_name: env.environment_name || '',
        environment_description: env.environment_description || '',
        url: env.target_url || '',
      });
    } catch (err) {
      console.error('Error fetching environment:', err);
      setLoadError(err?.message || 'Failed to load environment');
    } finally {
      setInitialLoading(false);
    }
  }, [envId, reset]);

  useEffect(() => {
    loadEnvironment();
  }, [loadEnvironment]);

  useEffect(() => {
    setUrlCheck({ status: null, message: '' });
  }, [url]);

  /** Best-effort reachability check; `no-cors` only tells us the host answered. */
  const checkUrl = async (value) => {
    if (!value || !isHttpUrl(value)) return;
    setUrlCheck({ status: 'checking', message: 'Checking the URL…' });
    try {
      await Promise.race([
        fetch(value, { method: 'HEAD', mode: 'no-cors' }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
      ]);
      setUrlCheck({ status: 'ok', message: 'The URL responded.' });
    } catch {
      setUrlCheck({
        status: 'fail',
        message: 'Could not reach this URL from your browser. You can still save it.',
      });
    }
  };

  const urlField = register('url', {
    required: 'URL is required',
    validate: (v) => isHttpUrl(v.trim()) || 'Enter a full URL starting with http:// or https://',
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = {
        env_id: isEdit ? parseInt(envId, 10) : undefined,
        project_id: parseInt(projectId, 10),
        environment_name: data.environment_name.trim(),
        environment_description: data.environment_description.trim(),
        url: data.url.trim(),
        is_wizard: false,
        environment_data: [],
      };
      const res = isEdit
        ? await axiosInstance.put('/user_profile/update_environment', payload)
        : await axiosInstance.post('/user_profile/add_environment_setting', payload);
      if (res?.status_code !== 200) throw new Error(res?.message || 'Failed to save environment');
      navigate(listPath());
    } catch (err) {
      console.error('Error saving environment:', err);
      toast.error(err?.response?.data?.detail?.message || err?.message || 'Failed to save environment');
    } finally {
      setSubmitting(false);
    }
  };

  const crumbs = (
    <Breadcrumbs
      items={[
        { label: 'Configure' },
        { label: 'Environments', path: 'list' },
        { label: isEdit ? 'Edit' : 'Create' },
      ]}
      onNavigate={() => navigate(listPath())}
    />
  );

  const title = isEdit ? 'Edit Environment' : 'Create Environment';

  if (initialLoading) {
    return (
      <PageScaffold title={title} breadcrumbs={crumbs} compact>
        <LoadingState label="Loading environment…" />
      </PageScaffold>
    );
  }

  if (loadError) {
    return (
      <PageScaffold title={title} breadcrumbs={crumbs} compact>
        <ErrorState title="Unable to open environment" description={loadError} onRetry={loadEnvironment} />
      </PageScaffold>
    );
  }

  const urlIsValid = isHttpUrl(url.trim());

  return (
    <PageScaffold
      title={title}
      description={
        isEdit
          ? 'Update the name, description or target URL of this environment.'
          : 'Add a runtime target so test runs know which application URL to open.'
      }
      breadcrumbs={crumbs}
    >
      <form className="rt-env-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="rt-env-form__main">
          <section className="rt-env-card">
            <header className="rt-env-card__header">
              <h2 className="rt-env-card__title">Details</h2>
              <p className="rt-env-card__subtitle">How this environment appears across plans and runs.</p>
            </header>

            <div className="rt-env-field">
              <label htmlFor="env-name" className="rt-env-field__label">
                Environment name <span className="rt-env-field__req">*</span>
              </label>
              <input
                id="env-name"
                type="text"
                className={`rt-env-input${errors.environment_name ? ' is-invalid' : ''}`}
                placeholder="e.g. Staging"
                autoFocus={!isEdit}
                aria-invalid={Boolean(errors.environment_name)}
                {...register('environment_name', {
                  required: 'Environment name is required',
                  validate: (v) => v.trim().length > 0 || 'Environment name is required',
                })}
              />
              {errors.environment_name && (
                <p className="rt-env-field__error">{errors.environment_name.message}</p>
              )}
            </div>

            <div className="rt-env-field">
              <div className="rt-env-field__label-row">
                <label htmlFor="env-desc" className="rt-env-field__label">
                  Description <span className="rt-env-field__req">*</span>
                </label>
                <span className="rt-env-field__counter">
                  {description.length}/{DESCRIPTION_MAX}
                </span>
              </div>
              <textarea
                id="env-desc"
                rows={3}
                maxLength={DESCRIPTION_MAX}
                className={`rt-env-input rt-env-input--textarea${errors.environment_description ? ' is-invalid' : ''}`}
                placeholder="e.g. Pre-release build used for regression runs"
                aria-invalid={Boolean(errors.environment_description)}
                {...register('environment_description', {
                  required: 'Description is required',
                  validate: (v) => v.trim().length > 0 || 'Description is required',
                })}
              />
              {errors.environment_description && (
                <p className="rt-env-field__error">{errors.environment_description.message}</p>
              )}
            </div>
          </section>

          <section className="rt-env-card">
            <header className="rt-env-card__header">
              <h2 className="rt-env-card__title">Target</h2>
              <p className="rt-env-card__subtitle">The page test runs open first, usually the login page.</p>
            </header>

            <div className="rt-env-field">
              <label htmlFor="env-url" className="rt-env-field__label">
                URL <span className="rt-env-field__req">*</span>
              </label>
              <div className="rt-env-url-input">
                <input
                  id="env-url"
                  type="url"
                  inputMode="url"
                  className={`rt-env-input${errors.url || urlCheck.status === 'fail' ? ' is-invalid' : ''}${
                    urlCheck.status === 'ok' ? ' is-valid' : ''
                  }`}
                  placeholder="https://staging.example.com/login"
                  aria-invalid={Boolean(errors.url)}
                  {...urlField}
                  onBlur={(e) => {
                    urlField.onBlur(e);
                    checkUrl(e.target.value.trim());
                  }}
                />
                <a
                  className={`rt-btn rt-btn--secondary${urlIsValid ? '' : ' is-disabled'}`}
                  href={urlIsValid ? url.trim() : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!urlIsValid}
                  tabIndex={urlIsValid ? 0 : -1}
                  onClick={(e) => !urlIsValid && e.preventDefault()}
                >
                  <LuExternalLink size={14} aria-hidden="true" /> Open
                </a>
              </div>
              {errors.url ? (
                <p className="rt-env-field__error">{errors.url.message}</p>
              ) : urlCheck.status ? (
                <p className={`rt-env-field__status rt-env-field__status--${urlCheck.status}`}>
                  {urlCheck.status === 'checking' && <LuLoaderCircle size={14} className="rt-env-spin" />}
                  {urlCheck.status === 'ok' && <LuCircleCheck size={14} />}
                  {urlCheck.status === 'fail' && <LuCircleX size={14} />}
                  {urlCheck.message}
                </p>
              ) : (
                <p className="rt-env-field__hint">Include the protocol, for example https://.</p>
              )}
            </div>
          </section>

          <div className="rt-env-form__actions">
            <button
              type="button"
              className="rt-btn rt-btn--secondary"
              onClick={() => navigate(listPath())}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rt-btn rt-btn--primary"
              disabled={submitting || (isEdit && !isDirty)}
            >
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create Environment'}
            </button>
          </div>
        </div>

        <aside className="rt-env-form__aside">
          <section className="rt-env-card rt-env-card--muted">
            <h2 className="rt-env-card__title">Tips</h2>
            <ul className="rt-env-tips">
              <li>Create one environment per deployment, such as Development, Staging and Production.</li>
              <li>Point the URL at the page a run should start on, usually the login screen.</li>
              <li>Test plans and runs let you choose which environment to target.</li>
            </ul>
          </section>
        </aside>
      </form>
    </PageScaffold>
  );
};

export default EnvironmentFormPage;
